import { apiClient } from './api';
import type { TicketQueryParams } from '@/types/api';

export interface ExportOptions {
  fromDate?: Date;
  toDate?: Date;
  agentId?: string;
  teamId?: string;
}

export type TicketExportOptions = TicketQueryParams;

export function buildTicketExportParams(options: TicketExportOptions): URLSearchParams {
  const params = new URLSearchParams();
  options.statuses?.forEach(status => params.append('Statuses', status));
  options.priorities?.forEach(priority => params.append('Priorities', priority));
  options.sources?.forEach(source => params.append('Sources', source));
  options.assignedAgentIds?.forEach(id => params.append('AssignedAgentIds', id));
  options.requesterIds?.forEach(id => params.append('RequesterIds', id));
  options.companyIds?.forEach(id => params.append('CompanyIds', id));
  options.tags?.forEach(tag => params.append('Tags', tag));
  if (options.searchTerm) params.append('SearchTerm', options.searchTerm);
  if (options.createdAfter) params.append('CreatedAfter', options.createdAfter);
  if (options.createdBefore) params.append('CreatedBefore', options.createdBefore);
  if (options.updatedAfter) params.append('UpdatedAfter', options.updatedAfter);
  if (options.updatedBefore) params.append('UpdatedBefore', options.updatedBefore);
  if (options.hasAttachments !== undefined) params.append('HasAttachments', String(options.hasAttachments));
  if (options.isEscalated !== undefined) params.append('IsEscalated', String(options.isEscalated));
  if (options.onlyMyTickets) params.append('OnlyMyTickets', 'true');
  if (options.onlyUnassigned) params.append('OnlyUnassigned', 'true');
  if (options.sortBy) params.append('SortBy', options.sortBy);
  if (options.sortDirection) params.append('SortDirection', options.sortDirection);
  return params;
}

export interface KnowledgeBaseExportOptions {
  publicOnly?: boolean;
  categoryId?: string;
  status?: string;
  type?: string;
  authorId?: string;
}

export interface EmailNotificationExportOptions extends ExportOptions {
  email?: string;
  status?: string;
  templateType?: string;
}

class ExportService {
  /**
   * Download a file from a blob response
   */
  private downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Format date for API query parameters
   */
  private formatDateForApi(date: Date): string {
    return date.toISOString();
  }

  /**
   * Build query parameters for export requests
   */
  private buildExportParams(options: ExportOptions): URLSearchParams {
    const params = new URLSearchParams();
    
    if (options.fromDate) {
      params.append('fromDate', this.formatDateForApi(options.fromDate));
    }
    
    if (options.toDate) {
      params.append('toDate', this.formatDateForApi(options.toDate));
    }
    
    if (options.agentId) {
      params.append('agentId', options.agentId);
    }
    
    if (options.teamId) {
      params.append('teamId', options.teamId);
    }
    
    return params;
  }

  /**
   * Export executive dashboard data to CSV
   */
  async exportExecutiveDashboard(options: ExportOptions = {}): Promise<void> {
    try {
      const params = this.buildExportParams(options);
      const response = await apiClient.axiosClient.get(`/api/v1/analytics/executive/dashboard/export?${params.toString()}`, {
        responseType: 'blob',
      });

      // Extract filename from response headers or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `executive-dashboard-export-${new Date().toISOString().slice(0, 16).replace('T', '-').replace(/:/g, '')}.csv`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      this.downloadFile(response.data, filename);
    } catch (error) {
      console.error('Failed to export executive dashboard:', error);
      throw new Error('Failed to export executive dashboard data');
    }
  }

  /**
   * Export agent performance data to CSV
   */
  async exportAgentPerformance(options: ExportOptions = {}): Promise<void> {
    try {
      const params = this.buildExportParams(options);
      const response = await apiClient.axiosClient.get(`/api/v1/analytics/agents/performance/export?${params.toString()}`, {
        responseType: 'blob',
      });

      const contentDisposition = response.headers['content-disposition'];
      let filename = `agent-performance-export-${new Date().toISOString().slice(0, 16).replace('T', '-').replace(/:/g, '')}.csv`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      this.downloadFile(response.data, filename);
    } catch (error) {
      console.error('Failed to export agent performance:', error);
      throw new Error('Failed to export agent performance data');
    }
  }

  /**
   * Export ticket analytics to CSV
   */
  async exportTicketAnalytics(options: ExportOptions = {}): Promise<void> {
    try {
      const params = this.buildExportParams(options);
      const response = await apiClient.axiosClient.get(`/api/v1/analytics/tickets/analytics/export?${params.toString()}`, {
        responseType: 'blob',
      });

      const contentDisposition = response.headers['content-disposition'];
      let filename = `ticket-analytics-export-${new Date().toISOString().slice(0, 16).replace('T', '-').replace(/:/g, '')}.csv`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      this.downloadFile(response.data, filename);
    } catch (error) {
      console.error('Failed to export ticket analytics:', error);
      throw new Error('Failed to export ticket analytics data');
    }
  }

  /**
   * Export response time analytics to CSV
   */
  async exportResponseTimeAnalytics(options: ExportOptions = {}): Promise<void> {
    try {
      const params = this.buildExportParams(options);
      const response = await apiClient.axiosClient.get(`/api/v1/analytics/response-time/export?${params.toString()}`, {
        responseType: 'blob',
      });

      const contentDisposition = response.headers['content-disposition'];
      let filename = `response-time-analytics-export-${new Date().toISOString().slice(0, 16).replace('T', '-').replace(/:/g, '')}.csv`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      this.downloadFile(response.data, filename);
    } catch (error) {
      console.error('Failed to export response time analytics:', error);
      throw new Error('Failed to export response time analytics data');
    }
  }

  /**
   * Export operational dashboard data to CSV
   */
  async exportOperationalDashboard(options: ExportOptions = {}): Promise<void> {
    try {
      const params = this.buildExportParams(options);
      const response = await apiClient.axiosClient.get(`/api/v1/analytics/operational/dashboard/export?${params.toString()}`, {
        responseType: 'blob',
      });

      const contentDisposition = response.headers['content-disposition'];
      let filename = `operational-dashboard-export-${new Date().toISOString().slice(0, 16).replace('T', '-').replace(/:/g, '')}.csv`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      this.downloadFile(response.data, filename);
    } catch (error) {
      console.error('Failed to export operational dashboard:', error);
      throw new Error('Failed to export operational dashboard data');
    }
  }

  /**
   * Export all reports data to CSV (combined export)
   */
  async exportAllReports(options: ExportOptions = {}): Promise<void> {
    try {
      // Export all available reports in sequence
      const exports = [
        this.exportExecutiveDashboard(options),
        this.exportAgentPerformance(options),
        this.exportTicketAnalytics(options),
        this.exportResponseTimeAnalytics(options),
        this.exportOperationalDashboard(options),
      ];

      await Promise.all(exports);
    } catch (error) {
      console.error('Failed to export all reports:', error);
      throw new Error('Failed to export all reports data');
    }
  }

  /**
   * Export tickets to CSV
   */
  async exportTickets(options: TicketExportOptions = {}): Promise<void> {
    try {
      const params = buildTicketExportParams(options);

      const response = await apiClient.axiosClient.get(`/api/v1/tickets/export?${params.toString()}`, {
        responseType: 'blob',
      });

      const contentDisposition = response.headers['content-disposition'];
      let filename = `tickets-export-${new Date().toISOString().slice(0, 16).replace('T', '-').replace(/:/g, '')}.csv`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      this.downloadFile(response.data, filename);
    } catch (error) {
      console.error('Failed to export tickets:', error);
      throw new Error('Failed to export tickets data');
    }
  }

  /**
   * Export knowledge base articles to CSV
   */
  async exportKnowledgeBase(options: KnowledgeBaseExportOptions = {}): Promise<void> {
    try {
      const params = new URLSearchParams();
      
      if (options.publicOnly !== undefined) params.append('publicOnly', options.publicOnly.toString());
      if (options.categoryId) params.append('categoryId', options.categoryId);
      if (options.status) params.append('status', options.status);
      if (options.type) params.append('type', options.type);
      if (options.authorId) params.append('authorId', options.authorId);

      const response = await apiClient.axiosClient.get(`/api/v1/knowledge/articles/export?${params.toString()}`, {
        responseType: 'blob',
      });

      const contentDisposition = response.headers['content-disposition'];
      let filename = `knowledge-base-export-${new Date().toISOString().slice(0, 16).replace('T', '-').replace(/:/g, '')}.csv`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      this.downloadFile(response.data, filename);
    } catch (error) {
      console.error('Failed to export knowledge base:', error);
      throw new Error('Failed to export knowledge base data');
    }
  }

  /**
   * Export SLA policies to CSV
   */
  async exportSLAPolicies(): Promise<void> {
    try {
      const response = await apiClient.axiosClient.get('/api/v1/sla/policies/export', {
        responseType: 'blob',
      });

      const contentDisposition = response.headers['content-disposition'];
      let filename = `sla-policies-export-${new Date().toISOString().slice(0, 16).replace('T', '-').replace(/:/g, '')}.csv`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      this.downloadFile(response.data, filename);
    } catch (error) {
      console.error('Failed to export SLA policies:', error);
      throw new Error('Failed to export SLA policies data');
    }
  }

  /**
   * Export email notifications to CSV
   */
  async exportEmailNotifications(options: EmailNotificationExportOptions = {}): Promise<void> {
    try {
      const params = new URLSearchParams();
      
      if (options.email) params.append('email', options.email);
      if (options.status) params.append('status', options.status);
      if (options.templateType) params.append('templateType', options.templateType);
      if (options.fromDate) params.append('startDate', this.formatDateForApi(options.fromDate));
      if (options.toDate) params.append('endDate', this.formatDateForApi(options.toDate));

      const response = await apiClient.axiosClient.get(`/api/v1/email/notifications/export?${params.toString()}`, {
        responseType: 'blob',
      });

      const contentDisposition = response.headers['content-disposition'];
      let filename = `email-notifications-export-${new Date().toISOString().slice(0, 16).replace('T', '-').replace(/:/g, '')}.csv`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      this.downloadFile(response.data, filename);
    } catch (error) {
      console.error('Failed to export email notifications:', error);
      throw new Error('Failed to export email notifications data');
    }
  }

  /**
   * Export users to CSV
   */
  async exportUsers(options: { search?: string; role?: string; isActive?: boolean } = {}): Promise<void> {
    try {
      const params = new URLSearchParams();
      
      if (options.search) params.append('search', options.search);
      if (options.role) params.append('role', options.role);
      if (options.isActive !== undefined) params.append('isActive', options.isActive.toString());

      const response = await apiClient.axiosClient.get(`/api/v1/users/export?${params.toString()}`, {
        responseType: 'blob',
      });

      const contentDisposition = response.headers['content-disposition'];
      let filename = `users-export-${new Date().toISOString().slice(0, 16).replace('T', '-').replace(/:/g, '')}.csv`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      this.downloadFile(response.data, filename);
    } catch (error) {
      console.error('Failed to export users:', error);
      throw new Error('Failed to export users data');
    }
  }
}

export const exportService = new ExportService();
